import axios from "axios";
import apiPath from "../constants/apiPath";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { ShowToast, Severty } from "../helper/toast";

const client = axios.create({
  baseURL: apiPath.baseURL,
});

/**
 * Extract a clean message from any backend error shape
 */
const extractMessageFromError = (err) => {
  if (!err) return "Something went wrong";

  // Axios network errors
  if (err.code === "ERR_NETWORK") {
    return "Network error. Please check your internet connection.";
  }

  // Axios-level error without response
  if (err.message && !err.response) {
    return err.message;
  }

  const resp = err.response;
  if (!resp) return err.message || "Something went wrong";

  const data = resp.data || {};

  if (data.status === false && typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }

  if (data.status === false && typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }

  // DRF ya generic errors
  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }

  if (Array.isArray(data.errors)) {
    return data.errors.map((e) => e?.message || e).join(", ");
  }
  if (typeof data.errors === "object") {
    const vals = Object.values(data.errors).flat();
    return vals.join(", ");
  }

  if (data.non_field_errors) {
    return Array.isArray(data.non_field_errors)
      ? data.non_field_errors.join(", ")
      : String(data.non_field_errors);
  }

  // Fallbacks
  if (resp.statusText) return resp.statusText;
  if (resp.status) return `Request failed with status ${resp.status}`;

  return err.message || "Something went wrong";
};


const useRequest = () => {
  const { logout } = useContext(AuthContext);

  const request = async ({
    url,
    method: tmethod = "GET",
    data,
    onSuccess,
    onError,
    header,
    onErrorSubmit,
  }) => {
    const method = (tmethod || "GET").trim().toUpperCase();

    const token = localStorage.getItem("token") || "";

    const headers = {
      ...header,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    try {
      const response = await client({
        url,
        method,
        data,
        headers,
      });

      // Success callback
      if (onSuccess) {
        onSuccess(response.data);
      } else if (onErrorSubmit) {
        onErrorSubmit(response.data);
      } else {
        console.log("[useRequest] Success:", response.data);
      }

      return response.data;
    } catch (err) {
      console.error("[useRequest] Error:", err);
      console.error("[useRequest] response.data:", err?.response?.data);

      const status = err?.response?.status;
      // if (status === 404) {
      //   console.warn("Resource not found, ignoring 404");
      //   return; // simply return without showing toast
      // }
      // Auto logout on expired/invalid session
      if (status === 401 || status === 403) {
        ShowToast("Session expired. Please login again.", Severty.ERROR);
        logout();
      }
      if (err?.response?.data?.message === "jwt expired") {
        ShowToast("Your session has expired. Please login again.", Severty.ERROR);
        logout();
      }

      // If caller gave a custom onError, let them handle
      if (onError) {
        onError(err);
        return;
      }

      // Otherwise use global fallback
      const message = extractMessageFromError(err);
      ShowToast(message, Severty.ERROR);
      return;
    }
  };

  return { request };
};

export default useRequest;
